import React, { createContext, useContext } from 'react'

// Optional badge shown after EVERY step heading when the form is
// subscription-only (a single, non-one_time frequency) — v7's take on
// v6's centered "Monthly donation" heading (donate_v6/Intro.jsx), which
// read as a stray giant title. It's a static, app-global fact (derived
// once from `frequencies`), so App provides it via this context and each
// PageHeader picks it up without every step threading the prop. null on
// forms that offer one-time giving, so the badge simply doesn't render.
const HeadingBadgeContext = createContext(null)

export const HeadingBadgeProvider = HeadingBadgeContext.Provider

// Small header at the top of each step panel. The widget-level h1 (donor
// → org name) lives in the partial outside the React app; these are h2s
// underneath that, so they read as section headings rather than competing
// with the page title.
//
// `subtitle` is optional fine print below the heading. Accepts either a
// string (auto-wrapped in the subtitle text style) or a ReactNode that
// the caller has already styled — Start.jsx passes the matching-banner
// JSX through this slot when an active match is present, CryptoInfo
// passes a dynamic string. Both render in the same vertical block as
// the heading and inherit the tight `space-y-0.5` rhythm; placing
// anything just outside the header would pick up the form's
// `space-y-4` gap, which is too loose for subtitle-style content.
const PageHeader = ({ heading, subtitle }) => {
  const badge = useContext(HeadingBadgeContext)
  return (
    <header className="space-y-0.5">
      <h2 className="text-lg font-semibold flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {heading}
        {badge && (
          // bg-brand is the same fill as the primary button
          // (--btn-primary-bg), so reuse --btn-primary-text for the
          // label: it's white on dark brands and flips to gray-900 on
          // light brands (_donations_styling sets it via
          // color_contrast). Hardcoding text-white would go illegible
          // on a light brand — the exact issue the button contrast work
          // already solved.
          <span className="inline-flex items-center rounded-full bg-brand text-(--btn-primary-text) px-2 py-0.5 text-xs font-semibold">
            {badge}
          </span>
        )}
      </h2>
      {subtitle && (
        typeof subtitle === 'string'
          ? <p className="text-sm text-gray-500">{subtitle}</p>
          : subtitle
      )}
    </header>
  )
}

export default PageHeader
